import { useMemo } from "react";

export const DEFAULT_HERO_ENTRANCE = "fade-up";

/**
 * Hook that consumes the serialized hero data and provides slot content
 * for automatic mode renderers.
 *
 * Returns null when the hero is in manual layout mode.
 * When active, provides the mode definition, slot assignments, unassigned items,
 * and helper functions for accessing slot content.
 *
 * Slot shape: ``slots[slotName]`` can be
 *   * ``null``              — single-value slot with no assignment
 *   * an object             — single-value slot with one assignment
 *   * an array (possibly empty) — multi-value slot (``max_count > 1``)
 *
 * ``getSlot`` preserves this shape unchanged. Renderers are expected to
 * check ``Array.isArray`` to handle multi-item slots (see HeroGallery
 * for the canonical example: it flattens a mix of ``image`` and
 * ``image_collection`` entries into a single track).
 *
 * ``animatedEntrance`` is the user-selected CSS animation id (e.g. "fade-up",
 * "slide-right"). It is always a concrete value because the backend field
 * defaults to ``"fade-up"``; however we still fall back to
 * ``DEFAULT_HERO_ENTRANCE`` defensively for pre-migration data or cases
 * where the serializer omits the field. Consumers should pass this value
 * directly to ``useHeroAnimation`` without additional translation.
 *
 * ``ambientAnimation`` is the *continuous* animation id declared by the
 * mode itself (``modeDefinition.ambient_animation``) — e.g. "ken-burns",
 * "float", "marquee-y". Unlike entrance animations this is NOT user-
 * selectable and NOT gated by ``hero_mode_animated``: each mode variant
 * picks its own natural ambient motion (paired static / animated
 * variants exist specifically so users opt into motion up-front).
 * ``null`` means "this mode has no ambient motion"; the
 * ``useHeroAmbient`` helper handles that cleanly (empty className).
 */
export default function useHeroSlots(item) {
  const isAutomatic = item?.manual_layout === false && !!item?.hero_mode;

  return useMemo(() => {
    if (!isAutomatic) return null;

    const modeDefinition = item?.active_mode_definition ?? null;
    const slotAssignments = item?.slot_assignments || {};
    const unassignedItems = item?.unassigned_items || [];

    const hasAssignment = (value) => {
      if (value == null) return false;
      if (Array.isArray(value)) return value.length > 0;
      return true;
    };

    return {
      modeDefinition,
      slots: slotAssignments,
      unassignedItems,
      isAnimated: item?.hero_mode_animated || false,
      animatedEntrance: item?.animated_entrance_choice || DEFAULT_HERO_ENTRANCE,
      ambientAnimation: modeDefinition?.ambient_animation ?? null,
      // Preserve array shape for multi-item slots; callers branch on
      // ``Array.isArray`` when they expect multiple assignments.
      getSlot: (slotName) => {
        const raw = slotAssignments[slotName];
        if (raw === undefined) return null;
        return raw;
      },
      hasSlot: (slotName) => hasAssignment(slotAssignments[slotName]),
    };
  }, [
    isAutomatic,
    item?.active_mode_definition,
    item?.slot_assignments,
    item?.unassigned_items,
    item?.hero_mode_animated,
    item?.animated_entrance_choice,
  ]);
}
